import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';
import navConfig from '../../components/sidebar/NavConfig';
import { isSameUserType } from '../../utils/helperFunction';
import { crmTypes, croTypeNames } from '../../utils/employeeTypes';

export const initialState = {
  loading: false,
  userProfile: {},
  navConfig: [
    // {
    //     title: "todo",
    //     path: "/todo",
    //     children: [
    //         {
    //             title: "My Tasks",
    //             path: "/todo/my-tasks",
    //         },
    //         {
    //             title: "Created Tasks",
    //             path: "/todo/created-tasks",
    //         },
    //         {
    //             title: "Department Tasks",
    //             path: "/todo/department-tasks",
    //         },
    //         {
    //             title: "Inward Tasks",
    //             path: "/todo/inward-tasks",
    //         },
    //         {
    //             title: "Outward Tasks",
    //             path: "/todo/outward-tasks",
    //         },
    //     ],
    // },

    // task Management is built in getNavConfig via ensureTaskManagementAutoTabs so that
    // its auto tabs merge into the manual permissions instead of being de-duped away.
    {
      title: 'employee Services',
      name: 'employeeServices',
      path: '/employeeservices',
      children: [
        {
          title: 'Attendance',
          path: '/employeeservices/attendance',
        },
        {
          title: 'Leave',
          path: '/employeeservices/leave',
        },
        {
          title: 'Compensatory Work',
          path: '/employeeservices/compensatory-work-request',
        },
        // {
        //   title: "Cabin Booking",
        //   path: "/employeeservices/cabin-booking",
        // },
        // {
        //     title: "MentoringBookings",
        //     path: "/employeeservices/mentoring-bookings",
        // },
        {
          title: 'Holiday Calendar',
          path: '/employeeservices/holidays-calendar',
        },
        {
          title: 'Food',
          path: '/employeeservices/food',
        },
        {
          title: 'WFH Grant Request',
          path: '/employeeservices/leave-grant-requests',
        },
        {
          title: 'Post Working',
          path: '/employeeservices/postWorking',
        },
        {
          title: 'Transportation',
          path: '/employeeservices/transportation',
        },
        {
          title: 'Requirement',
          path: '/employeeservices/requirement',
        },
        {
          title: 'Regularization Request',
          path: '/employeeservices/attendance-regularization',
        },
        {
          title: 'HR Policy',
          path: '/employeeservices/hr-policy',
        },
          {
          title: 'Employee LMS',
          path: '/employeeservices/employee-lms',
        },
      ],
    },

    // {
    //     title: "privileges",
    //     path: "/privileges",
    // },
    // {
    //     title: "reffer And Earn",
    //     name: "reffer",
    //     path: "/reffer",
    //     children: [
    //         {
    //             title: "Add Referral",
    //             path: "/reffer/addrefferel",
    //         },
    //         {
    //             title: "Referral Status",
    //             path: "/reffer/refferelStatus",
    //         },
    //     ],
    // },
  ],
  otherConfig: [],
  permissions: [],
  navChild: [],
};

// Task Management auto tabs — always visible, independent of the manual permissions
// returned by the API. Manual tabs granted on /task-management are merged alongside
// these, and never replace them.
const getTaskManagementAutoTabs = (userProfile) => [
  {
    title: 'My tasks',
    path: '/task-management/my-tasks',
  },
  {
    title: 'Task Analytics',
    path: '/task-management/user-task-analytics',
  },
  ...(userProfile?.isReportingHead
    ? [
        {
          title: 'Department Analytics',
          path: '/task-management/head-analytics',
        },
      ]
    : []),
];

// Injects the auto tabs into the task management module of the given nav config.
// If the module is absent (no manual permission at all) it is created, otherwise the
// auto tabs are appended to the existing (manual) children without removing them.
const ensureTaskManagementAutoTabs = (navConfig, userProfile) => {
  const autoTabs = getTaskManagementAutoTabs(userProfile);

  const taskManagement = navConfig.find(
    (item) =>
      item?.name === 'taskManagement' ||
      item?.path === '/task-management' ||
      item?.title?.toLowerCase() === 'task management',
  );

  if (!taskManagement) {
    navConfig.push({
      title: 'task Management',
      name: 'taskManagement',
      path: '/task-management',
      children: autoTabs,
    });
    return navConfig;
  }

  taskManagement.children = taskManagement.children || [];
  autoTabs.forEach((tab) => {
    if (!taskManagement.children.some((child) => child.path === tab.path)) {
      taskManagement.children.push(tab);
    }
  });

  return navConfig;
};

// Retention workflow sidebar group (RETENTION_WORKFLOW.md §2.4) — auto tabs for CROs and CRMs.
// `order` keeps the demo's order instead of the alphabetical sort applied to every other group.
// Module path is /student-journey so the group also stays open inside a student's workspace (/student-journey/:id).
const getRetentionAutoTabs = () => [
  { title: "Today's tasks", path: '/student-journey/tasks', order: 1 },
  { title: 'Reports', path: '/student-journey/reports', order: 2 },
  { title: 'Retained students', path: '/student-journey/retained', order: 3 },
  { title: 'Exited students', path: '/student-journey/exited', order: 4 },
];

const ensureRetentionAutoTabs = (navConfig, userProfile) => {
  if (!isSameUserType({ roleArray: [...croTypeNames, ...crmTypes], role: userProfile?.role })) return navConfig;

  const retention = navConfig.find(
    (item) => item?.name === 'retention' || item?.path === '/student-journey' || item?.path === '/retention'
  );
  if (!retention) {
    navConfig.push({ title: 'Retention', name: 'retention', path: '/student-journey', children: getRetentionAutoTabs() });
    return navConfig;
  }

  // /retention is the API prefix — a stored nav entry still on it would collide with the backend on refresh
  retention.path = '/student-journey';
  retention.children = (retention.children || []).filter((child) => !child?.path?.startsWith('/retention'));
  getRetentionAutoTabs().forEach((tab) => {
    if (!retention.children.some((child) => child.path === tab.path)) retention.children.push(tab);
  });
  return navConfig;
};

// Team Workload (Technical Department) — a Dashboard tab for every department 5 user, independent of the manual
// permissions: who may see what on it is decided by the server (tech-workload/*). The title is also the key
// DashboardRouter checks in navChild before it registers the route.
export const TECH_WORKLOAD_TITLE = 'Team Workload';
const TECH_WORKLOAD_PATH = 'dashboard/tech-workload';

const ensureTechWorkloadTab = (navConfig, userProfile) => {
  if (Number(userProfile?.departmentId) !== 5) return navConfig;

  let dashboard = navConfig.find((item) => String(item?.title || '').toLowerCase() === 'dashboard');
  if (!dashboard) {
    dashboard = { title: 'dashboard', path: '/dashboard', children: [] };
    navConfig.push(dashboard);
  }
  dashboard.children = dashboard.children || [];
  if (!dashboard.children.some((child) => child?.title === TECH_WORKLOAD_TITLE)) {
    dashboard.children.push({ title: TECH_WORKLOAD_TITLE, path: TECH_WORKLOAD_PATH });
  }
  return navConfig;
};

export const getNavConfig = createAsyncThunk('role/nav-config', async () => {
  const res = await apiRequest({
    method: 'GET',
    url: 'permissions/byUserType',
  });
  const userProfile = JSON.parse(localStorage.getItem('cred')).profile;
  let flag = false;
  res.data.navConfig.forEach((item) => {
    // console.log(item.title);
    if (item.title === 'my workspace') {
      flag = true;
    }
  });

  // const isTech=res.data.other.find((data)=>data.title=="tech support") //TECH SUPPORT IS ADDING TO SIDE BAR IF THE PERSON IS TECH ADDITIONALLY GENERATED REQUEST CHILDREN WILL BE ADDED OTHER WISE ONLY SHOW REQUEST SUPPORT
  const isTech = userProfile?.departmentId == 5;
  res.data.navConfig.push({
    title: 'Help And Support',
    name: 'helpandsupport',
    path: '/helpandsupport',
    children: [
      ...(!isTech
        ? [
            {
              title: 'Request support',
              path: '/helpandsupport/raise-request',
            },
          ]
        : []),
      ...(isTech
        ? [
            {
              title: 'Generated Requests',
              path: '/helpandsupport/generated-requests',
            },
          ]
        : []),
    ],
  });

  if (userProfile.isReportingHead && !flag) {
    res.data.navConfig.push({
      id: 3,
      path: '/my-workspace',
      title: 'my workspace',
      children: [],
    });
  }

  // Grievance
  if (
    isSameUserType({
      roleArray: [...croTypeNames, ...crmTypes, 'project manager'],
      role: userProfile?.role,
    })
  ) {
    res.data.navConfig.push({
      id: 931,
      path: 'Grievance',
      title: 'Grievance',
      children: [
        {
          title: 'Tickets',
          path: 'Grievance/nav-grievance-tickets',
        },
      ],
    });
  }

  res.data.navConfig.forEach((item) => {
    if (item.title === 'my workspace') {
      // Employee Engagement is company-wide — visible to EVERY user.
      item.children.push({
        title: 'Employee Engagement',
        path: 'my-workspace/engagement',
      });
    }
    if (item.title === 'my workspace' && userProfile.isReportingHead) {
      item.children.push(
        {
          title: 'department Leaves',
          path: 'my-workspace/reporting_head_or_hod/hod-leave-applications',
        },
        {
          title: 'department Attendance',
          path: 'my-workspace/reporting_head_or_hod/reporting-head-attendance',
        },
        {
          title: 'compensatory work request',
          path: 'my-workspace/reporting-head-comp-work-req',
        },
        {
          title: 'department wfh grants',
          path: 'my-workspace/hod-leave-grant-requests',
        },
        {
          title: 'department regularization',
          path: 'my-workspace/attendance-regularization',
        },
      );
    }
  });

  // Task Management: My tasks + Task Analytics for everyone, Department Analytics for
  // reporting heads. Merged into the module returned by the API so that a manual
  // permission on /task-management never hides them.
  ensureTaskManagementAutoTabs(res.data.navConfig, userProfile);
  ensureRetentionAutoTabs(res.data.navConfig, userProfile);
  ensureTechWorkloadTab(res.data.navConfig, userProfile);

  let IsStudentPortalPermissions = res.data.navConfig.find((item) => item.title == 'student portal'); //IF THE STUDENT PORTAL OBJECT IS PRESENT THEN IT HAS THEE PERMISIONS AND IT WILL DISPALY IF ITS NOT PESENT THEN ASSIGNED TICKETS WILL BE ADDED LIKE GLOBALLY
  if (!IsStudentPortalPermissions) {
    res.data.navConfig.push({
      id: 7,
      path: '/student-portal',
      title: 'student portal',
      children: [
        {
          title: 'Assigned tickets',
          path: '/student-portal/assigned-tickets',
        },
      ],
    });
  }

  const appraisalManagement = res.data.navConfig.find((item) => item?.id === 9);
  if (!appraisalManagement) {
    res.data.navConfig.push({
      title: 'appraisal Management',
      name: 'AppraisalManagement',
      path: '/appraisal-management',
      children: [
        {
          title: 'Appraisal',
          path: '/appraisal-management/appraisal',
        },
      ],
    });
  } else {
    appraisalManagement?.children?.push({
      title: 'Appraisal',
      path: '/appraisal-management/appraisal',
    });
  }

  return res;
});

const roleSlice = createSlice({
  name: 'role',
  initialState: initialState,
  reducers: {
    settingPermissions: (state, action) => {
      action.payload.forEach((item) => {
        state.permissions.push(item.title);
      });
      return state;
    },
    filterNavChild: (state, action) => {
      const navBar = state.navConfig?.filter((item) => item.title === action.payload);
      if (navBar.length > 0) {
        navBar[0].children?.forEach((item) => {
          state.navChild.push(item.title);
        });
      }
      return state;
    },
    cleanUpPermissions: (state) => {
      return {
        ...state,
        permissions: [],
        navChild: [],
      };
    },
    clearRoles: (state) => {
      state = initialState;
      return state;
    },
    saveUserProfile: (state) => {
      return {
        ...state,
        userProfile: JSON.parse(localStorage.getItem('cred')).profile,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNavConfig.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getNavConfig.fulfilled, (state, action) => {
      const combinedNavConfig = [...action.payload.data.navConfig, ...state.navConfig];

      const uniqueNavConfig = combinedNavConfig.filter(
        (item, index, self) => index === self.findIndex((t) => t.title.toLowerCase() === item.title.toLowerCase()),
      );

      // Re-assert the task management auto tabs on the merged list, so they survive the
      // title based de-duplication above whichever entry (API or local) wins.
      try {
        const profile = JSON.parse(localStorage.getItem('cred')).profile;
        ensureTaskManagementAutoTabs(uniqueNavConfig, profile);
        ensureRetentionAutoTabs(uniqueNavConfig, profile);
        ensureTechWorkloadTab(uniqueNavConfig, profile);
      } catch (e) {
        // ignore JSON parse errors
      }

      // sort sidebar
      uniqueNavConfig?.forEach?.((item) => {
        item?.children.sort((a, b) => {
          // explicit order wins (retention group); everything else stays alphabetical
          if (a.order != null && b.order != null) return a.order - b.order;
          const nameA = a.title.trim().toUpperCase();
          const nameB = b.title.trim().toUpperCase();

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }

          return 0;
        });
      });

      state.navConfig = uniqueNavConfig;
      state.otherConfig = action.payload.data.other;
      state.loading = false;
    });
  },
});
export const { settingPermissions, cleanUpPermissions, filterNavChild, saveUserProfile, clearRoles } =
  roleSlice.actions;
export default roleSlice.reducer;
